import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobile } from '../hooks/useMobile';
import Sidebar from '../components/Sidebar';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';
const tok = () => localStorage.getItem('token');

const fmt     = n => Number(n || 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
const fmtFull = n => Number(n || 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 });

const STAGE_META = {
  pipeline:  { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', label: 'Pipeline' },
  completed: { bg: '#d1fae5', color: '#065f46', dot: '#10b981', label: 'Completed' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: 'Cancelled' },
};

function StageBadge({ stage }) {
  const m = STAGE_META[stage] || { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af', label: stage };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
      padding: '3px 9px', borderRadius: 20, background: m.bg, color: m.color }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, display: 'inline-block' }} />
      {m.label}
    </span>
  );
}

function StatCard({ icon, label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px',
      border: '1px solid #e5e7eb', borderLeft: `4px solid ${accent}`,
      display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0e1b2c', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function AllocPill({ label, value, color }) {
  if (!value || parseFloat(value) === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color }}>{fmt(value)}</div>
    </div>
  );
}

export default function OwnerPipeline({ noLayout = false } = {}) {
  const navigate = useNavigate();
  const mobile = useMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedBroker, setExpandedBroker] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');

  useEffect(() => {
    fetch(`${API}/owner/company-pipeline`, { headers: { Authorization: `Bearer ${tok()}` } })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError('Failed to load pipeline: ' + e.message); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 14, color: '#6b7280', flexDirection: 'column' }}>
      <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <p style={{ margin: 0, fontSize: 14 }}>Loading pipeline…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ margin: noLayout ? '24px 32px' : 40, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 18px', color: '#dc2626' }}>
      ⚠️ {error}
    </div>
  );

  const { summary, brokers } = data;
  const networkFeePct = summary.gross > 0 ? ((summary.networkFee / summary.gross) * 100).toFixed(1) : '0';

  const _inner = (
    <main style={{ flex: 1, padding: noLayout ? '24px 32px' : (mobile ? '24px 20px' : '32px 40px'), width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

      {/* Header — hidden in noLayout */}
      {!noLayout && (
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0e1b2c', margin: 0 }}>Company Pipeline</h1>
          <p style={{ color: '#6b7280', marginTop: 4, fontSize: 14 }}>All deals across your team with net commission breakdown</p>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard icon="📁" label="Total Deals"      value={summary.total}    sub={`${summary.pipeline} active · ${summary.completed} completed`} accent="#6366f1" />
        <StatCard icon="💷" label="Gross Case Value"  value={fmt(summary.gross)}                    accent="#0e1b2c" />
        {summary.networkFee > 0 && <StatCard icon="🏦" label={`Network Fee (${networkFeePct}%)`} value={`− ${fmt(summary.networkFee)}`} accent="#f59e0b" />}
        <StatCard icon="✅" label="Net Case Value"    value={fmt(summary.net)} accent="#10b981" />
      </div>

      {/* Commission allocations bar */}
      {(summary.companyAlloc > 0 || summary.managerAlloc > 0 || summary.brokerAlloc > 0) && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
          padding: '16px 24px', marginBottom: 24, display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Commission split:</span>
          <AllocPill label="Company"  value={summary.companyAlloc}  color="#6366f1" />
          <AllocPill label="Manager"  value={summary.managerAlloc}  color="#f59e0b" />
          <AllocPill label="Broker"   value={summary.brokerAlloc}   color="#10b981" />
        </div>
      )}

      {/* Stage filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'pipeline', 'completed'].map(s => (
          <button key={s} onClick={() => setStageFilter(s)}
            style={{ padding: '7px 18px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: stageFilter === s ? 'none' : '1px solid #e5e7eb',
              background: stageFilter === s ? '#0e1b2c' : '#fff',
              color: stageFilter === s ? '#fff' : '#6b7280',
              transition: 'all 0.15s' }}>
            {s === 'all' ? 'All Deals' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Broker accordion cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {brokers.map(broker => {
          const filteredDeals = stageFilter === 'all' ? broker.deals : broker.deals.filter(d => d.stage === stageFilter);
          if (filteredDeals.length === 0) return null;
          const isExpanded = expandedBroker === broker.broker_user_id;
          const hasNetworkFee = broker.totals.networkFee > 0;

          return (
            <div key={broker.broker_user_id || 'unassigned'}
              style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden',
                boxShadow: isExpanded ? '0 2px 8px rgba(0,0,0,0.06)' : 'none', transition: 'box-shadow 0.15s' }}>

              {/* Broker header row */}
              <div onClick={() => setExpandedBroker(isExpanded ? null : broker.broker_user_id)}
                style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', cursor: 'pointer',
                  gap: 14, userSelect: 'none', borderBottom: isExpanded ? '1px solid #f3f4f6' : 'none' }}>

                {/* Avatar */}
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#0e1b2c',
                  color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                  {(broker.broker_name || '?').charAt(0).toUpperCase()}
                </div>

                {/* Name + level */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0e1b2c' }}>{broker.broker_name}</div>
                  {broker.broker_level && (
                    <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize', marginTop: 1 }}>{broker.broker_level}</div>
                  )}
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Deals</div>
                    <div style={{ fontWeight: 700, color: '#0e1b2c', fontSize: 15 }}>
                      {filteredDeals.length}
                      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 400, marginLeft: 4 }}>({broker.totals.pipeline} active)</span>
                    </div>
                  </div>
                  {hasNetworkFee && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Gross</div>
                      <div style={{ fontWeight: 600, color: '#0e1b2c', fontSize: 15 }}>{fmt(broker.totals.gross)}</div>
                    </div>
                  )}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{hasNetworkFee ? 'Net Value' : 'Case Value'}</div>
                    <div style={{ fontWeight: 700, color: '#10b981', fontSize: 17 }}>{fmt(broker.totals.net)}</div>
                  </div>
                  {broker.totals.brokerAlloc > 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Commission</div>
                      <div style={{ fontWeight: 700, color: '#6366f1', fontSize: 15 }}>{fmt(broker.totals.brokerAlloc)}</div>
                    </div>
                  )}
                </div>

                <div style={{ color: '#9ca3af', fontSize: 13, marginLeft: 4, flexShrink: 0 }}>{isExpanded ? '▲' : '▼'}</div>
              </div>

              {/* Deal table */}
              {isExpanded && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        {['Ref', 'Client', 'Source', 'Stage', 'Proc Fee', 'Client Fee',
                          hasNetworkFee ? 'Network Fee' : null,
                          'Net Value', 'Company', 'Manager', 'Broker', '']
                          .filter(Boolean).map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11,
                            fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
                            letterSpacing: '0.04em', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeals.map((deal, i) => (
                        <tr key={deal.id}
                          style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                          onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafafa'}>
                          <td style={{ padding: '11px 14px', color: '#6366f1', fontWeight: 700 }}>{deal.reference || '—'}</td>
                          <td style={{ padding: '11px 14px', color: '#0e1b2c', fontWeight: 600 }}>{deal.client_name}</td>
                          <td style={{ padding: '11px 14px' }}>
                            {deal.introducer_name
                              ? <span style={{ color: '#6366f1', fontSize: 12, fontWeight: 500 }}>↗ {deal.introducer_name}</span>
                              : <span style={{ color: '#9ca3af', fontSize: 12 }}>Direct</span>}
                          </td>
                          <td style={{ padding: '11px 14px' }}><StageBadge stage={deal.stage} /></td>
                          <td style={{ padding: '11px 14px', color: '#374151' }}>{deal.lender_proc_fee ? fmtFull(deal.lender_proc_fee) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                          <td style={{ padding: '11px 14px', color: '#374151' }}>{deal.client_fee ? fmtFull(deal.client_fee) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                          {hasNetworkFee && <td style={{ padding: '11px 14px', color: '#f59e0b', fontWeight: 600 }}>{deal.network_fee > 0 ? `− ${fmtFull(deal.network_fee)}` : <span style={{ color: '#d1d5db' }}>—</span>}</td>}
                          <td style={{ padding: '11px 14px', fontWeight: 700, color: '#10b981' }}>{fmtFull(deal.net_case_value)}</td>
                          <td style={{ padding: '11px 14px', color: '#6366f1', fontWeight: 600 }}>{parseFloat(deal.company_alloc || 0) > 0 ? fmtFull(deal.company_alloc) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                          <td style={{ padding: '11px 14px', color: '#f59e0b', fontWeight: 600 }}>{parseFloat(deal.manager_alloc || 0) > 0 ? fmtFull(deal.manager_alloc) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                          <td style={{ padding: '11px 14px', color: '#10b981', fontWeight: 600 }}>{parseFloat(deal.broker_alloc || 0) > 0 ? fmtFull(deal.broker_alloc) : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                          <td style={{ padding: '11px 14px' }}>
                            <button onClick={e => { e.stopPropagation(); navigate(`/deal/${deal.id}`); }}
                              style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '5px 12px',
                                fontSize: 12, cursor: 'pointer', background: '#fff', color: '#374151', fontWeight: 500 }}>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f0fdf4', fontWeight: 700, borderTop: '2px solid #d1fae5' }}>
                        <td colSpan={hasNetworkFee ? 7 : 6}
                          style={{ padding: '11px 14px', color: '#374151', fontSize: 12 }}>
                          {broker.broker_name} — {filteredDeals.length} deal{filteredDeals.length !== 1 ? 's' : ''}
                        </td>
                        <td style={{ padding: '11px 14px', color: '#10b981', fontSize: 14 }}>{fmt(filteredDeals.reduce((s, d) => s + d.net_case_value, 0))}</td>
                        <td style={{ padding: '11px 14px', color: '#6366f1' }}>{fmt(filteredDeals.reduce((s, d) => s + parseFloat(d.company_alloc || 0), 0))}</td>
                        <td style={{ padding: '11px 14px', color: '#f59e0b' }}>{fmt(filteredDeals.reduce((s, d) => s + parseFloat(d.manager_alloc || 0), 0))}</td>
                        <td style={{ padding: '11px 14px', color: '#10b981' }}>{fmt(filteredDeals.reduce((s, d) => s + parseFloat(d.broker_alloc || 0), 0))}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );

  if (noLayout) return _inner;
  return <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}><Sidebar />{_inner}</div>;
}
